import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="select"
export default class extends Controller {
  static targets = ["hiddenInput", "searchInput", "option", "noResults", "optionsContainer", "menu"]
  static values = {
    searchUrl: String,
    debounce: { type: Number, default: 300 }
  }

  connect() {
    this.debounceTimer = null
    this.setupMenuObserver()
  }

  disconnect() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
  }

  handleSelect(event) {
    const { value } = event.detail

    if (this.hasHiddenInputTarget) {
      this.hiddenInputTarget.value = value
      // Trigger change event for form validation
      this.hiddenInputTarget.dispatchEvent(new Event('change', { bubbles: true }))
    }

    // Reset search when selection is made
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = ""
      if (this.hasSearchUrlValue) {
        this.fetchOptions("")
      } else {
        this.showAllOptions()
      }
    }
  }

  filterOptions() {
    if (!this.hasSearchInputTarget) return

    // Clear existing timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }

    // If search URL is provided, fetch from server
    if (this.hasSearchUrlValue) {
      this.debounceTimer = setTimeout(() => {
        const searchTerm = this.searchInputTarget.value.trim()
        this.fetchOptions(searchTerm)
      }, this.debounceValue)
    } else {
      // Otherwise, filter client-side
      this.filterOptionsClientSide()
    }
  }

  filterOptionsClientSide() {
    if (!this.hasOptionTargets) return

    const searchTerm = this.searchInputTarget.value.toLowerCase().trim()
    let visibleCount = 0

    this.optionTargets.forEach(option => {
      const searchText = option.dataset.searchText || option.dataset.text.toLowerCase()
      const matches = searchText.includes(searchTerm)

      if (matches) {
        option.classList.remove('hidden')
        visibleCount++
      } else {
        option.classList.add('hidden')
      }
    })

    // Show/hide "no results" message
    this.updateNoResultsMessage(visibleCount)
  }

  async fetchOptions(searchTerm) {
    if (!this.hasSearchUrlValue) return

    try {
      // Show loading state
      this.showLoading()

      const url = new URL(this.searchUrlValue, window.location.origin)
      url.searchParams.set('q', searchTerm)

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': csrfToken
        }
      })

      if (!response.ok) throw new Error('Network response was not ok')

      const data = await response.json()
      this.updateOptions(data)
    } catch (error) {
      console.error('Error fetching options:', error)
      this.showError()
    }
  }

  showLoading() {
    if (!this.hasOptionsContainerTarget) return

    this.optionsContainerTarget.innerHTML = `
      <div class="w-full px-3 py-4 text-sm text-center text-gray-500">
        <div class="inline-flex items-center gap-2">
          <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span>Searching...</span>
        </div>
      </div>
    `

    if (this.hasNoResultsTarget) {
      this.noResultsTarget.classList.add('hidden')
    }
  }

  showError() {
    if (!this.hasOptionsContainerTarget) return

    this.optionsContainerTarget.innerHTML = `
      <div class="w-full px-3 py-4 text-sm text-center text-red-600">
        Error loading results. Please try again.
      </div>
    `
  }

  updateOptions(options) {
    if (!this.hasOptionsContainerTarget) return

    if (options.length === 0) {
      this.updateNoResultsMessage(0)
      this.optionsContainerTarget.innerHTML = ''
      return
    }

    // Create wrapper div for options
    const wrapper = document.createElement('div')
    wrapper.className = 'py-1 w-full'

    // Add new options
    options.forEach(option => {
      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'w-full text-left px-3 py-2 text-[#1F2937] hover:bg-[#fafafb] transition-colors text-sm'
      button.dataset.dropdownTarget = 'item'
      button.dataset.selectTarget = 'option'
      button.dataset.value = option.value
      button.dataset.text = option.label
      button.dataset.searchText = option.label.toLowerCase()
      button.dataset.action = 'click->dropdown#select'
      button.textContent = option.label

      wrapper.appendChild(button)
    })

    // Clear and add new options
    this.optionsContainerTarget.innerHTML = ''
    this.optionsContainerTarget.appendChild(wrapper)

    this.updateNoResultsMessage(options.length)
  }

  updateNoResultsMessage(visibleCount) {
    if (!this.hasNoResultsTarget) return

    if (visibleCount === 0) {
      this.noResultsTarget.classList.remove('hidden')
      if (this.hasOptionsContainerTarget) {
        this.optionsContainerTarget.classList.add('hidden')
      }
    } else {
      this.noResultsTarget.classList.add('hidden')
      if (this.hasOptionsContainerTarget) {
        this.optionsContainerTarget.classList.remove('hidden')
      }
    }
  }

  showAllOptions() {
    if (!this.hasOptionTargets) return

    this.optionTargets.forEach(option => {
      option.classList.remove('hidden')
    })

    if (this.hasNoResultsTarget) {
      this.noResultsTarget.classList.add('hidden')
    }
    if (this.hasOptionsContainerTarget) {
      this.optionsContainerTarget.classList.remove('hidden')
    }
  }

  // Focus search input when menu opens
  setupMenuObserver() {
    if (this.hasMenuTarget && this.hasSearchInputTarget) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'hidden') {
            const isHidden = this.menuTarget.hasAttribute('hidden')
            if (!isHidden && this.hasSearchInputTarget) {
              setTimeout(() => this.searchInputTarget.focus(), 100)
            }
          }
        })
      })

      observer.observe(this.menuTarget, { attributes: true })
    }
  }
}
